import { Button, Checkbox, cn, FormField, FormFieldHint, FormFieldLabel } from '@eureca/ui';
import { ProgramEditShell, type ProgramEditShellProps } from '../program-edit/ProgramEditShell';
import { BlockedEditTooltip } from '../program-edit/BlockedEditTooltip';
import { PlusIcon } from './icons';

export interface ProgramEditBenefitsPageProps extends Omit<
  ProgramEditShellProps,
  'subtitle' | 'activeStep' | 'children'
> {
  benefitOptions: readonly string[];
  selectedBenefits: string[];
  onToggleBenefit: (benefit: string) => void;
  onAddBenefit: () => void;

  additionalPaymentOptions: readonly string[];
  selectedAdditionalPayments: string[];
  onToggleAdditionalPayment: (payment: string) => void;
  onAddAdditionalPayment: () => void;
}

export function ProgramEditBenefitsPage({
  benefitOptions,
  selectedBenefits,
  onToggleBenefit,
  onAddBenefit,
  additionalPaymentOptions,
  selectedAdditionalPayments,
  onToggleAdditionalPayment,
  onAddAdditionalPayment,
  ...shellProps
}: ProgramEditBenefitsPageProps) {
  // EU-570 — Encerrado/Cancelado: pills + "Adicionar novo" bloqueados com o
  // tooltip "Edição bloqueada" (o fieldset do shell desabilita os controles).
  const readOnly = shellProps.readOnly ?? false;
  return (
    <ProgramEditShell {...shellProps} subtitle="Benefícios" activeStep="benefits">
      <BlockedEditTooltip enabled={readOnly}>
        <FormField>
          <FormFieldLabel>Benefícios</FormFieldLabel>
          <CheckboxCardGroup
            options={benefitOptions}
            selected={selectedBenefits}
            onToggle={onToggleBenefit}
          />
          {/* EU-709 RN-06 — a etapa pode ficar inteiramente vazia. */}
          <FormFieldHint>Esse campo é opcional</FormFieldHint>
          <Button
            type="button"
            variant="secondary"
            size="lg"
            onClick={onAddBenefit}
            className="self-start"
          >
            <PlusIcon />
            Adicionar novo
          </Button>
        </FormField>
      </BlockedEditTooltip>

      <BlockedEditTooltip enabled={readOnly}>
        <FormField>
          <FormFieldLabel>Pagamento adicionais</FormFieldLabel>
          <CheckboxCardGroup
            options={additionalPaymentOptions}
            selected={selectedAdditionalPayments}
            onToggle={onToggleAdditionalPayment}
          />
          <FormFieldHint>Esse campo é opcional</FormFieldHint>
          <Button
            type="button"
            variant="secondary"
            size="lg"
            onClick={onAddAdditionalPayment}
            className="self-start"
          >
            <PlusIcon />
            Adicionar novo
          </Button>
        </FormField>
      </BlockedEditTooltip>
    </ProgramEditShell>
  );
}

interface CheckboxCardGroupProps {
  options: readonly string[];
  selected: string[];
  onToggle: (value: string) => void;
}

function CheckboxCardGroup({ options, selected, onToggle }: CheckboxCardGroupProps) {
  return (
    <div className="flex flex-row flex-wrap gap-2">
      {options.map((option) => {
        const isChecked = selected.includes(option);
        return (
          <CheckboxCardPill
            key={option}
            label={option}
            checked={isChecked}
            onCheckedChange={() => onToggle(option)}
          />
        );
      })}
    </div>
  );
}

interface CheckboxCardPillProps {
  label: string;
  checked: boolean;
  onCheckedChange: () => void;
}

function CheckboxCardPill({ label, checked, onCheckedChange }: CheckboxCardPillProps) {
  return (
    <label
      className={cn(
        'group inline-flex cursor-pointer select-none items-center gap-2 rounded-xl border bg-card px-4 py-3 text-sm font-medium transition-colors',
        checked
          ? 'border-primary bg-primary/10 text-foreground'
          : 'border-border text-foreground hover:border-primary'
      )}
    >
      <Checkbox checked={checked} onCheckedChange={onCheckedChange} className="size-4" />
      <span>{label}</span>
    </label>
  );
}
